import React, { useState, useCallback, useRef, useMemo } from "react";
import { MIDI_MIN, MIDI_MAX, midiToNoteName, isBlackKey } from "./midiConstants";

const WHITE_KEY_WIDTH = 28;
const BLACK_KEY_WIDTH = 18;

// Reverse map: computer key → semitone offset
const LOWER_ROW = { z: 0, s: 1, x: 2, d: 3, c: 4, v: 5, g: 6, b: 7, h: 8, n: 9, j: 10, m: 11 };
const UPPER_ROW = { q: 12, "2": 13, w: 14, "3": 15, e: 16, r: 17, "5": 18, t: 19, "6": 20, y: 21, "7": 22, u: 23 };

function buildKeyLabels(baseOctave) {
  const labels = {};
  const all = { ...LOWER_ROW, ...UPPER_ROW };
  for (const [key, offset] of Object.entries(all)) {
    const midi = (baseOctave + 1) * 12 + offset;
    if (midi >= MIDI_MIN && midi <= MIDI_MAX) {
      labels[midi] = key.toUpperCase();
    }
  }
  return labels;
}

export default function PianoKeyboard({ onNoteDown, baseOctave = 3 }) {
  const [activeKeys, setActiveKeys] = useState(new Set());
  const pointerDownRef = useRef(false);

  const keyLabels = useMemo(() => buildKeyLabels(baseOctave), [baseOctave]);

  // Build white and black key arrays with positions
  const { whiteKeys, blackKeys, totalWidth } = useMemo(() => {
    const whites = [];
    const blacks = [];
    let whiteIdx = 0;

    for (let midi = MIDI_MIN; midi <= MIDI_MAX; midi++) {
      if (isBlackKey(midi)) {
        // Black key centered on the boundary before this white key index
        blacks.push({ midi, left: whiteIdx * WHITE_KEY_WIDTH - BLACK_KEY_WIDTH / 2 });
      } else {
        whites.push({ midi, left: whiteIdx * WHITE_KEY_WIDTH });
        whiteIdx++;
      }
    }

    return { whiteKeys: whites, blackKeys: blacks, totalWidth: whiteIdx * WHITE_KEY_WIDTH };
  }, []);

  const triggerNote = useCallback((midi) => {
    setActiveKeys((prev) => new Set(prev).add(midi));
    onNoteDown?.(midi);
  }, [onNoteDown]);

  const releaseNote = useCallback((midi) => {
    setActiveKeys((prev) => {
      const next = new Set(prev);
      next.delete(midi);
      return next;
    });
  }, []);

  const handlePointerDown = useCallback((midi, e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointerDownRef.current = true;
    triggerNote(midi);
  }, [triggerNote]);

  const handlePointerUp = useCallback((midi) => {
    pointerDownRef.current = false;
    releaseNote(midi);
  }, [releaseNote]);

  const handlePointerEnter = useCallback((midi) => {
    // Glide: play notes as you drag across keys
    if (pointerDownRef.current) {
      triggerNote(midi);
    }
  }, [triggerNote]);

  const handlePointerLeave = useCallback((midi) => {
    releaseNote(midi);
  }, [releaseNote]);

  const renderKey = (midi, left, isBlack) => {
    const isActive = activeKeys.has(midi);
    const label = keyLabels[midi];
    const noteName = midiToNoteName(midi);
    const isC = midi % 12 === 0;
    const cls = isBlack ? "black" : "white";

    return (
      <div
        key={midi}
        className={
          `daw-kbd__key daw-kbd__key--${cls}` +
          (isActive ? " daw-kbd__key--active" : "")
        }
        style={{ left, width: isBlack ? BLACK_KEY_WIDTH : WHITE_KEY_WIDTH }}
        onPointerDown={(e) => handlePointerDown(midi, e)}
        onPointerUp={() => handlePointerUp(midi)}
        onPointerEnter={() => handlePointerEnter(midi)}
        onPointerLeave={() => handlePointerLeave(midi)}
      >
        {label && (
          <span className="daw-kbd__shortcut">{label}</span>
        )}
        {!isBlack && isC && (
          <span className="daw-kbd__note-name">{noteName}</span>
        )}
      </div>
    );
  };

  return (
    <div className="daw-kbd">
      <div className="daw-kbd__keys" style={{ width: totalWidth }}>
        {/* White keys first (z-index lower) */}
        {whiteKeys.map(({ midi, left }) => renderKey(midi, left, false))}
        {/* Black keys on top */}
        {blackKeys.map(({ midi, left }) => renderKey(midi, left, true))}
      </div>
    </div>
  );
}
